import React, { createContext, useContext, useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { trackPurchase } from '../lib/analytics';

const StripeContext = createContext();

export const useStripeContext = () => useContext(StripeContext);

export const StripeProvider = ({ children }) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const [daysRemaining, setDaysRemaining] = useState(null);

  useEffect(() => {
    // Initialize Stripe
    const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    // console.log('Stripe key exists:', !!stripeKey);
    // console.log('Stripe key starts with pk_:', stripeKey?.startsWith('pk_'));
    
    if (!stripeKey) {
      setError('Stripe publishable key is not configured. Please contact support.');
      return;
    }
    
    if (!stripeKey.startsWith('pk_')) {
      setError('Invalid Stripe publishable key format. Please contact support.');
      return;
    }

    setStripePromise(
      loadStripe(stripeKey)
    );
  }, []);

  useEffect(() => {
    // Check payment status when user changes
    const checkPaymentStatus = async () => {
      if (user) {
        try {
          const { data, error } = await supabase
            .from('payments')
            .select('status, created_at, amount')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1);

          if (error) {
            console.error('Error fetching payment status:', error);
            setError('Failed to fetch payment status. Please try again later.');
            setPaymentStatus(null);
            return;
          }

          if (data && data.length > 0) {
            const payment = data[0];
            const purchaseDate = new Date(payment.created_at);
            // Default to 7 days for all payments since we don't have plan_id
            const expirationDate = new Date(purchaseDate.getTime() + 7 * 24 * 60 * 60 * 1000);
            const now = new Date();
            const remainingDays = Math.ceil((expirationDate - now) / (1000 * 60 * 60 * 24));
            setDaysRemaining(remainingDays);

            if (now > expirationDate) {
              // Update payment status to expired
              const { error: updateError } = await supabase
                .from('payments')
                .update({ status: 'expired' })
                .eq('id', payment.id);

              if (updateError) {
                console.error('Error updating payment status:', updateError);
                setError('Failed to update payment status. Please try again later.');
              }
              setPaymentStatus('expired');
            } else {
              setPaymentStatus(payment.status);
              // Track successful payment
              if (payment.status === 'paid' && payment.amount) {
                trackPurchase('Showtime', payment.amount);
              }
            }
          } else {
            setPaymentStatus(null);
          }
        } catch (err) {
          console.error('Unexpected error checking payment status:', err);
          setError('An unexpected error occurred. Please try again later.');
          setPaymentStatus(null);
        }
      } else {
        // Clear payment status when user is null (logged out)
        setPaymentStatus(null);
        setDaysRemaining(null);
      }
    };

    checkPaymentStatus();
  }, [user]);

  // Check URL for session_id after payment
  useEffect(() => {
    const checkSessionStatus = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      
      if (sessionId) {
        try {
          // First, check if the payment exists
          const { data: paymentData, error: paymentError } = await supabase
            .from('payments')
            .select('status, user_id')
            .eq('stripe_session_id', sessionId)
            .maybeSingle();

          if (paymentError) {
            console.error('Error verifying payment:', paymentError);
            setError('Failed to verify payment. Please contact support.');
            return;
          }

          if (!paymentData) {
            // Payment record doesn't exist yet, wait and retry
            console.log('Payment record not found, waiting for webhook...');
            setTimeout(() => {
              checkSessionStatus();
            }, 2000); // Retry after 2 seconds
            return;
          }

          // Update payment status
          setPaymentStatus(paymentData.status);

          // Clear the session_id from URL and navigate to slideshow
          const newUrl = window.location.origin + '/slideshow';
          window.history.replaceState({}, '', newUrl);
          
          // Force a refresh of the payment status
          const checkPaymentStatus = async () => {
            if (user) {
              try {
                const { data, error } = await supabase
                  .from('payments')
                  .select('status, created_at')
                  .eq('user_id', user.id)
                  .order('created_at', { ascending: false })
                  .limit(1);

                if (!error && data && data.length > 0) {
                  setPaymentStatus(data[0].status);
                }
              } catch (err) {
                console.error('Error refreshing payment status:', err);
              }
            }
          };

          // Wait a moment for the webhook to process, then check status
          setTimeout(checkPaymentStatus, 3000);
        } catch (error) {
          console.error('Error checking session status:', error);
          setError('An error occurred while processing your payment. Please contact support.');
        }
      }
    };

    checkSessionStatus();
  }, [user]);

  const createCheckoutSession = async (priceId) => {
    try {
      setError(null);
      
      if (!user) {
        throw new Error('You must be logged in to make a purchase.');
      }

      if (!priceId) {
        throw new Error('Invalid price ID. Please try again later.');
      }

      // Get the current origin (domain)
      const successUrl = `${window.location.origin}/slideshow`;
      const cancelUrl = `${window.location.origin}/pricing`;

      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session found. Please log in again.');
      }

      // Call the Edge Function to create a checkout session
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          price_id: priceId,
          user_id: user.id,
          success_url: successUrl,
          cancel_url: cancelUrl
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const data = await response.json();

      // Get the Stripe instance
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Payment system is not properly configured. Please contact support.');
      }

      // Redirect to Stripe Checkout
      const { error: redirectError } = await stripe.redirectToCheckout({
        sessionId: data.sessionId
      });

      if (redirectError) {
        console.error('Error redirecting to checkout:', redirectError);
        throw new Error('Failed to redirect to payment page. Please try again later.');
      }
    } catch (error) {
      console.error('Error in createCheckoutSession:', error);
      setError(error.message);
    }
  };

  const value = {
    stripePromise,
    paymentStatus,
    error,
    createCheckoutSession,
    daysRemaining,
  };

  return (
    <StripeContext.Provider value={value}>
      {children}
    </StripeContext.Provider>
  );
}; 